import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, Database, RefreshCw } from 'lucide-react';
import type { BackupStatus, ServerState, ServerUpdateRun } from '@hoopify/shared';
import { api, errorMessage } from '../../lib/api';
import { formatDateTime, timeAgo } from '../../lib/format';
import { Alert, Button, Dialog, LoadingBlock, Spinner } from '../../components/ui';
import { BackupLine } from './AdminOverviewPage';

export const serverKey = ['admin', 'server'] as const;
const STEPS = ['Checking GitHub', 'Backing up', 'Building', 'Restarting', 'Checking it started'];
const AUTO_CHECK_MS = 10 * 60_000;

const isBusy = (s: ServerState | undefined) =>
  !!s && s.available && (s.requested.update || (s.update?.state === 'running' && Date.now() - Date.parse(s.update.updatedAt) < 45 * 60_000));

/** Anything the server hasn't finished yet: poll quickly until it has. */
const isPending = (s: ServerState | undefined) => isBusy(s) || (!!s && s.available && (s.requested.check || s.requested.backup));

/** The server's state, polled quickly while it's working (the app restarts part way through an update). */
export function useServerState() {
  return useQuery({
    queryKey: serverKey,
    queryFn: () => api.get<ServerState>('/admin/server'),
    refetchInterval: (q) => (isPending(q.state.data) || q.state.fetchFailureCount > 0 ? 2000 : 60_000),
    retry: false,
  });
}

export function AdminServerPage() {
  const qc = useQueryClient();
  const q = useServerState();
  const backups = useQuery({ queryKey: ['admin', 'backups'], queryFn: () => api.get<BackupStatus>('/admin/backups'), refetchInterval: 15_000 });
  const [confirming, setConfirming] = useState(false);
  const ask = useMutation({
    mutationFn: (what: 'update' | 'backup' | 'check') => api.post<ServerState>(`/admin/server/${what}`),
    onSuccess: (s) => qc.setQueryData(serverKey, s),
  });

  // Check GitHub on opening the page, if the last check is a while ago.
  const autoChecked = useRef(false);
  const s = q.data;
  useEffect(() => {
    if (autoChecked.current || !s?.available || s.requested.check || isBusy(s)) return;
    const last = s.updates ? Date.parse(s.updates.checkedAt) : 0;
    if (Date.now() - last > AUTO_CHECK_MS) {
      autoChecked.current = true;
      ask.mutate('check');
    }
  }, [s, ask]);

  // Once the server finishes a check or backup, refresh what's shown.
  const wasPending = useRef(false);
  useEffect(() => {
    const pending = isPending(s);
    if (wasPending.current && !pending) void qc.invalidateQueries({ queryKey: ['admin'] });
    wasPending.current = pending;
  }, [s, qc]);

  if (q.isLoading) return <LoadingBlock />;
  if (!s) return <Alert>{errorMessage(q.error)}</Alert>;

  const busy = isBusy(s);
  // Mid-update the app restarts, so requests fail for a few seconds.
  const reconnecting = q.isError && isBusy(q.data);

  return (
    <>
      <h1 className="text-[24px] font-semibold tracking-[-0.01em]">Server</h1>
      <p className="mb-5 text-muted">Put new code from GitHub live, and back up the database.</p>

      {!s.available ? (
        <Alert tone="info">This page works on the Hoopify server. You’re running version {s.version} somewhere else (probably your PC).</Alert>
      ) : (
        <div className="space-y-6">
          {s.updates?.error && <Alert>{s.updates.error}</Alert>}

          <section className="rounded-lg border border-line bg-surface">
            <div className="flex flex-wrap items-start gap-3 px-4 py-4">
              <div className="min-w-0 flex-1 basis-[260px]">
                <p className="label-caps text-muted">Running</p>
                <p className="mt-1 font-mono text-[18px] font-semibold">{s.updates?.current.version ?? s.version}</p>
                {s.updates?.current.subject && (
                  <p className="text-[14px] text-muted">
                    {s.updates.current.subject}
                    {s.updates.current.date && ` · ${formatDateTime(s.updates.current.date)}`}
                  </p>
                )}
              </div>
              <Button variant="secondary" onClick={() => ask.mutate('check')} loading={s.requested.check} disabled={busy || s.requested.check}>
                <RefreshCw className="size-4" aria-hidden /> Check for updates
              </Button>
            </div>
            <div className="border-t border-line px-4 py-4">
              {busy || reconnecting ? (
                <Progress run={s.update} requested={s.requested.update} reconnecting={reconnecting} />
              ) : s.updates && s.updates.behind > 0 ? (
                <>
                  <p className="font-semibold">
                    {s.updates.behind === 1 ? '1 update is' : `${s.updates.behind} updates are`} ready to go live
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {s.updates.commits.map((c) => (
                      <li key={c.sha} className="flex gap-3 text-[14px]">
                        <span className="font-mono text-muted">{c.sha}</span>
                        <span className="min-w-0 flex-1">{c.subject}</span>
                        <span className="shrink-0 text-muted">{timeAgo(c.date)}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="mt-4" onClick={() => setConfirming(true)}>
                    Update now
                  </Button>
                </>
              ) : (
                <p className="flex items-center gap-2 text-[14px]">
                  <Check className="size-4 text-ok" aria-hidden />
                  {s.updates ? `Up to date. Checked GitHub ${timeAgo(s.updates.checkedAt)}.` : 'Not checked yet.'}
                </p>
              )}
            </div>
          </section>

          {!busy && !reconnecting && s.update && s.update.state !== 'up_to_date' && <LastUpdate run={s.update} log={s.log} />}

          <section className="rounded-lg border border-line bg-surface px-4 py-4">
            <div className="flex flex-wrap items-start gap-3">
              <div className="min-w-0 flex-1 basis-[260px]">
                <h2 className="font-semibold">Database backups</h2>
                <p className="text-[14px] text-muted">Every night at 3:30am, and before every update.</p>
                {backups.data && <BackupLine b={backups.data} compact />}
              </div>
              <Button variant="secondary" onClick={() => ask.mutate('backup')} loading={s.requested.backup} disabled={s.requested.backup || busy}>
                <Database className="size-4" aria-hidden /> {s.requested.backup ? 'Backing up' : 'Back up now'}
              </Button>
            </div>
          </section>
          {ask.isError && <Alert>{errorMessage(ask.error)}</Alert>}
          <p className="text-[13px] text-muted">
            Restoring a backup, changing settings and viewing logs are done in the Vultr console. See “Things done in the Vultr console” in docs/deploy-vultr.md.
          </p>
        </div>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)} title="Update Hoopify now?">
        <p className="text-muted">
          The database is backed up first, then the new version is built while the current one keeps running. Hoopify is offline for about 10 seconds while it restarts. If the new version doesn’t start, it goes back to this one by itself.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setConfirming(false)}>Cancel</Button>
          <Button
            loading={ask.isPending}
            onClick={() => {
              ask.mutate('update', { onSettled: () => setConfirming(false) });
            }}
          >
            Update now
          </Button>
        </div>
      </Dialog>
    </>
  );
}

function Progress({ run, requested, reconnecting }: { run: ServerUpdateRun | null; requested: boolean; reconnecting: boolean }) {
  const current = reconnecting ? 3 : requested || !run || run.state !== 'running' ? -1 : Math.max(0, STEPS.indexOf(run.step));
  return (
    <div aria-live="polite">
      <p className="flex items-center gap-2 font-semibold">
        <Spinner />
        {reconnecting ? 'Restarting. This page reconnects by itself.' : current < 0 ? 'Starting the update…' : `Updating${run?.to ? ` to ${run.to}` : ''}`}
      </p>
      <ol className="mt-3 space-y-1.5 text-[14px]">
        {STEPS.map((step, i) => (
          <li key={step} className={`flex items-center gap-2 ${i > current ? 'text-muted' : ''}`}>
            {i < current ? <Check className="size-4 text-ok" aria-hidden /> : i === current ? <Spinner /> : <span className="inline-block size-4" />}
            {step}
            {step === 'Building' && i === current && <span className="text-muted">(a few minutes; Hoopify stays up meanwhile)</span>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function LastUpdate({ run, log }: { run: ServerUpdateRun; log: string | null }) {
  const when = timeAgo(run.updatedAt);
  return (
    <section>
      {run.state === 'done' ? (
        <Alert tone="ok">Updated to {run.to} {when}.</Alert>
      ) : run.state === 'rolled_back' ? (
        <Alert tone="warn">{run.message} ({when})</Alert>
      ) : run.state === 'failed' ? (
        <Alert>
          The last update didn’t finish ({when}): {run.message}
        </Alert>
      ) : (
        <Alert tone="warn">
          The last update stopped reporting part way ({run.step}, {when}). <CircleAlert className="inline size-4" aria-hidden /> Check the server in the Vultr console.
        </Alert>
      )}
      {log && (
        <details className="mt-2">
          <summary className="cursor-pointer text-[14px] text-action">What happened</summary>
          <pre className="mt-2 max-h-80 overflow-auto rounded-md bg-sunken p-3 text-[12px] leading-relaxed whitespace-pre-wrap">{log}</pre>
        </details>
      )}
    </section>
  );
}

/** For the overview: a nudge when new code is waiting. */
export function UpdatesWaiting({ s }: { s: ServerState }) {
  if (!s.available || !s.updates || isBusy(s)) return null;
  if (s.updates.error) {
    return (
      <Alert>
        Hoopify can’t check GitHub for updates: {s.updates.error}{' '}
        <Link to="/admin/server" className="font-medium underline">Server</Link>
      </Alert>
    );
  }
  if (s.updates.behind === 0) return null;
  return (
    <Alert tone="info">
      {s.updates.behind === 1 ? '1 update is' : `${s.updates.behind} updates are`} ready to go live.{' '}
      <Link to="/admin/server" className="font-medium underline">Update from the Server page</Link>
    </Alert>
  );
}
